import { useState, useCallback, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

export interface UseProfilePhotoProps {
  maxSize?: number;
  onChange?: (file: File | null) => void;
  value?: File | string | null;
}

export function useProfilePhoto({
  maxSize = 5 * 1024 * 1024,
  onChange,
  value,
}: UseProfilePhotoProps = {}) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setImagePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof value === 'string') {
      setImagePreview(value);
    } else {
      setImagePreview(null);
    }
  }, [value]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const errorMessages = fileRejections
          .map((rejection) => rejection.errors.map((error) => error.message))
          .flat();
        setError(errorMessages.join('. '));
        return;
      }

      const file = acceptedFiles[0];
      if (file) {
        setError(null);
        onChange?.(file);
      }
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    maxSize,
    multiple: false,
    noClick: true,
  });

  const removeImage = useCallback(() => {
    setError(null);
    onChange?.(null);
  }, [onChange]);

  return {
    imagePreview,
    error,
    getRootProps,
    getInputProps,
    isDragActive,
    removeImage,
  };
}
